/**
 * normalizeCardData.js
 *
 * Normalizes all content types (activity, collection, board, walking_tour, trip, album)
 * into a consistent data shape for the UnifiedCard component.
 *
 * This ensures that regardless of the data source (API, saved items, social feed),
 * the UnifiedCard receives a predictable structure.
 */

import { getCategoryIconName, getCategoryDisplayName } from './categoryUtils';
import { resolveEventUrl, isTicketUrl, isNavigableUrl } from './eventLinks';
import { TRANSPORT_PRESENTATION, FOOT_CATEGORIES } from '../config/experienceCategories';
import { formatTimeAgo, parseLocalDate } from './dateUtils';
import { API_BASE } from '../config/api';
import { buildGooglePhotoProxyUrl } from './googlePhotoProxy';
import { attributionForImageUrl } from './imageSource';
import { deriveVenueFrequency } from './venueFrequency';
import { mergeAlbumMedia } from './albumMedia';

// Resolve relative /api/ paths to full URLs (needed when frontend and backend are on different ports)
const resolveImageUrl = (url) => {
  if (url && typeof url === 'string' && url.startsWith('/api/')) {
    return `${API_BASE}${url}`;
  }
  return url;
};

/**
 * Content type constants
 */
export const CONTENT_TYPES = {
  ACTIVITY: 'activity',
  COLLECTION: 'collection',
  BOARD: 'board',
  WALKING_TOUR: 'walking_tour',
  TRIP: 'trip',
  ALBUM: 'album',
  EVENT_GROUP: 'event_group',
  ARTIST_EVENT: 'artist_event',
  EVENT: 'event',
  VISIT_PHOTO_SET: 'visit_photo_set',
  TOURISM_EXPERIENCE: 'tourism_experience',
  VIATOR_EXPERIENCE: 'viator_experience',
  TIQETS_EXPERIENCE: 'tiqets_experience'
};

/**
 * Context constants - determines action row behavior
 */
export const CARD_CONTEXTS = {
  DISCOVER: 'discover',  // Social feed - shows creator, save actions
  SAVED: 'saved',        // My saved items - shows owner actions
  BOARD: 'board',        // Travel board view - shows remove action
  TRIP: 'trip',          // Trip planner view
  SEARCH: 'search'       // Search results
};

/**
 * Badge configurations by content type
 */
const TYPE_BADGES = {
  [CONTENT_TYPES.ACTIVITY]: { icon: 'mapPin', label: 'Activity', bgClass: 'bg-primary-lighter', textClass: 'text-primary-dark' },
  [CONTENT_TYPES.COLLECTION]: { icon: 'collection', label: 'Collection', bgClass: 'bg-warning-light', textClass: 'text-warning-dark' },
  [CONTENT_TYPES.BOARD]: { icon: 'clipboard', label: 'Board', bgClass: 'bg-background', textClass: 'text-content-secondary' },
  [CONTENT_TYPES.WALKING_TOUR]: { icon: 'walking', label: 'Walking Tour', bgClass: 'bg-info-light', textClass: 'text-info-dark' },
  [CONTENT_TYPES.TRIP]: { icon: 'airplane', label: 'Trip', bgClass: 'bg-info-light', textClass: 'text-info-dark' },
  [CONTENT_TYPES.ALBUM]: { icon: 'camera', label: 'Album', bgClass: 'bg-primary-lighter', textClass: 'text-primary-dark' },
  [CONTENT_TYPES.EVENT_GROUP]: { icon: 'theater', label: 'Event Series', bgClass: 'bg-primary-lighter', textClass: 'text-primary-dark' },
  [CONTENT_TYPES.ARTIST_EVENT]: { icon: 'ticket', label: 'Artist Event', bgClass: 'bg-primary-lighter', textClass: 'text-primary-dark' },
  [CONTENT_TYPES.EVENT]: { icon: 'ticket', label: 'Event', bgClass: 'bg-primary-lighter', textClass: 'text-primary-dark' },
  [CONTENT_TYPES.VISIT_PHOTO_SET]: { icon: 'camera', label: 'Visit', bgClass: 'bg-error-light', textClass: 'text-error-dark' },
  [CONTENT_TYPES.TOURISM_EXPERIENCE]: { icon: 'train', label: 'Experience', bgClass: 'bg-success-light', textClass: 'text-success-dark' },
  [CONTENT_TYPES.VIATOR_EXPERIENCE]: { icon: 'ticket', label: 'Experience', bgClass: 'bg-info-light', textClass: 'text-info-dark' },
  [CONTENT_TYPES.TIQETS_EXPERIENCE]: { icon: 'ticket', label: 'Experience', bgClass: 'bg-warning-light', textClass: 'text-warning-dark' }
};

/**
 * Get badge configuration for a content type
 * @param {string} type - Content type
 * @returns {Object} Badge configuration
 */
export const getTypeBadge = (type) => {
  return TYPE_BADGES[type] || TYPE_BADGES[CONTENT_TYPES.ACTIVITY];
};

/**
 * Gradient configurations for placeholder images by content type.
 *
 * NOT DRAWN since the 2026-10-01 visual refresh: PlaceholderImage and the
 * carousel placeholders are flat tints and ignore this value. Kept because the
 * normalized card shape still carries `gradient`. Originally: brand-token only
 * so cards read as one family, not a rainbow. Differentiation comes from the icon + label
 * in PlaceholderImage, not the fill color. Walking tours use the `tour` token,
 * which now aliases to the primary blue (the bespoke violet was retired), so this
 * resolves the same as BRAND_GRADIENT — kept via the token for one-line re-theming.
 */
const BRAND_GRADIENT = 'from-primary to-primary-dark';
const TYPE_GRADIENTS = {
  [CONTENT_TYPES.ACTIVITY]: BRAND_GRADIENT,
  [CONTENT_TYPES.COLLECTION]: BRAND_GRADIENT,
  [CONTENT_TYPES.BOARD]: BRAND_GRADIENT,
  [CONTENT_TYPES.WALKING_TOUR]: 'from-tour to-tour-dark',
  [CONTENT_TYPES.TRIP]: BRAND_GRADIENT,
  [CONTENT_TYPES.ALBUM]: BRAND_GRADIENT,
  [CONTENT_TYPES.EVENT_GROUP]: 'from-primary-dark via-primary to-primary-light',
  [CONTENT_TYPES.ARTIST_EVENT]: 'from-primary-lighter to-primary-light',
  [CONTENT_TYPES.EVENT]: 'from-primary-lighter to-primary-light',
  [CONTENT_TYPES.VISIT_PHOTO_SET]: BRAND_GRADIENT,
  [CONTENT_TYPES.TOURISM_EXPERIENCE]: BRAND_GRADIENT,
  [CONTENT_TYPES.VIATOR_EXPERIENCE]: BRAND_GRADIENT,
  [CONTENT_TYPES.TIQETS_EXPERIENCE]: BRAND_GRADIENT
};

/**
 * Get gradient class for placeholder image
 * @param {string} type - Content type
 * @returns {string} Tailwind gradient classes
 */
export const getTypeGradient = (type) => {
  return TYPE_GRADIENTS[type] || TYPE_GRADIENTS[CONTENT_TYPES.ACTIVITY];
};

/**
 * Host-derived credit for a carousel slide with no explicit attribution.
 * attributionForImageUrl returns either a bare string or a { text, uri }
 * object (the cover path renders the object form); slides render through
 * PhotoCredit, which takes the TEXT and derives the Commons link from the
 * photo URL itself — so the string is all a slide needs.
 */
const slideAttributionFallback = (url) => {
  const derived = attributionForImageUrl(url);
  if (!derived) return null;
  return typeof derived === 'object' ? derived.text : derived;
};

/**
 * Normalize photo data into consistent carousel format
 * @param {Object} item - Raw item data
 * @param {string} type - Content type
 * @returns {Object} Normalized carousel data
 */
const normalizeCarouselData = (item, type) => {
  const photos = [];
  let coverPhoto = null;
  let totalPhotos = 0;

  // Extract photos based on content type
  if (type === CONTENT_TYPES.ACTIVITY || type === 'place') {
    // Activity can have: photos array, imageUrl, cover_photo, resolved_photo
    if (item.photos && Array.isArray(item.photos)) {
      photos.push(...item.photos.map(p => {
        const url = p.image_data || p.url || p.photo_data_base64;
        return {
          id: p.id,
          url,
          iscover: p.is_activity_cover === 1,
          // Wikimedia/Tiqets slides travel with their credit — for the CC
          // BY-SA corpus the line is a licence condition, and the carousel
          // slide renders photo.attribution when present. The host-derived
          // fallback covers the slide the server prepends for an EXTERNAL
          // cover (which carries no attribution field): before carousels,
          // the single-cover branch credited those via photoAttribution, and
          // a Commons cover must not lose its credit by gaining slides.
          attribution: p.attribution || slideAttributionFallback(url)
        };
      }));
    }

    coverPhoto = item.resolved_photo ||
                 item.imageUrl ||
                 item.image ||
                 item.cover_photo ||
                 item.data?.imageUrl ||
                 item.data?.image;

    totalPhotos = item.total_photos || photos.length;
  } else if (type === CONTENT_TYPES.COLLECTION || type === CONTENT_TYPES.BOARD) {
    // Collections/Boards have preview_items with cover_photo
    if (item.previewDetails || item.preview_items || item.preview_activities) {
      const raw = item.previewDetails || item.preview_items || item.preview_activities || [];
      const previewItems = Array.isArray(raw) ? raw : [];
      photos.push(...previewItems.map((p, idx) => {
        const url = p.cover_photo || p.image || p.preview_image || p.imageUrl || p.data?.imageUrl;
        return {
          id: p.id || idx,
          url,
          title: p.title || p.name || p.data?.title,
          category: p.category,
          rating: p.rating,
          location: p.location,
          contentType: p.content_type || 'activity',
          // Preview tiles carry no attribution field; a Commons/Tiqets-hosted
          // cover still needs its credit line (licence condition).
          attribution: p.photo_attribution || slideAttributionFallback(url)
        };
      }));
    }
    coverPhoto = item.cover_photo || item.cover_image;
    totalPhotos = photos.length > 0 ? photos.length : (item.item_count || 0);
  } else if (type === CONTENT_TYPES.WALKING_TOUR) {
    // Parse stop photos from social feed query (GROUP_CONCAT'd JSON objects)
    if (item.stop_photos_json) {
      const stopPhotos = item.stop_photos_json.split('|||').map((jsonStr, idx) => {
        try {
          const parsed = JSON.parse(jsonStr);
          let url = parsed.photo_url;
          if (!url && parsed.google_photo_ref && parsed.place_id) {
            url = buildGooglePhotoProxyUrl(parsed.google_photo_ref, parsed.place_id);
          }
          if (!url) return null;
          // Credit the row that IS the rendered photo, never the other one.
          // Most of this corpus is CC BY-SA, where attribution is a licence
          // condition — a carousel slide showing one of these must print it.
          // Non-wikimedia winners (metadata/custom images) get the
          // host-derived minimum when their URL is Commons-hosted anyway.
          const attribution = url === parsed.poi_photo_url ? parsed.poi_attribution
            : url === parsed.place_photo_url ? parsed.place_attribution
            : slideAttributionFallback(url);
          return { id: `stop-${idx}`, url, title: parsed.stop_title, order: parsed.stop_order || idx, attribution };
        } catch { return null; }
      }).filter(Boolean).slice(0, 6);
      photos.push(...stopPhotos);
    }
    // Fallback: pre-populated stops array (detail page)
    if (!photos.length && item.stops && Array.isArray(item.stops)) {
      photos.push(...item.stops.slice(0, 6).map((stop, idx) => {
        const url = stop.cover_photo || stop.image;
        return {
          id: stop.id || idx,
          url,
          title: stop.name || stop.title,
          order: stop.order_index || idx,
          // Detail-page stops carry the exact credit (stopPhotoCredit);
          // otherwise fall back to the host-derived minimum.
          attribution: stop.photoAttribution?.text || slideAttributionFallback(url)
        };
      }).filter(p => p.url));
    }
    coverPhoto = item.cover_photo || item.cover_image;
    totalPhotos = photos.length || item.stop_count || 0;
  } else if (type === CONTENT_TYPES.TRIP) {
    // Trips: prefer backend-resolved cascade (highlights → owner photos → albums → fallback)
    coverPhoto = item.coverImage || item.cover_photo || item.coverPhoto;
    if (item.tripCardPhotos && Array.isArray(item.tripCardPhotos) && item.tripCardPhotos.length > 0) {
      // tripCardPhotos entries carry no attribution field, but the step-4
      // cascade fallback can resolve a Commons photo for a photoless linked
      // place — derive the required credit from the host rather than render
      // a CC BY-SA file uncredited.
      photos.push(...item.tripCardPhotos.slice(0, 6).map(p => ({
        ...p,
        attribution: p.attribution || slideAttributionFallback(p.url)
      })));
    } else if (item.activities && Array.isArray(item.activities)) {
      photos.push(...item.activities.slice(0, 6).map((a, idx) => {
        const url = a.cover_photo || a.image;
        return {
          id: a.id || idx,
          url,
          title: a.title || a.name,
          attribution: slideAttributionFallback(url)
        };
      }));
    }
    // Last resort: destination cover (Wikimedia/curated photo of the trip's
    // primary destination). Fills the blue+arrow placeholder before users
    // upload their own photos. Prefer thumb_url (500px) for card contexts.
    if (!coverPhoto && photos.length === 0 && item.destinationCoverPhoto?.photo_url) {
      coverPhoto = item.destinationCoverPhoto.thumb_url || item.destinationCoverPhoto.photo_url;
    }
    // The slides themselves, never activity_count: the carousel prints
    // "N of total" and a "+remaining more" overlay from this, so a stop count
    // read "2 of 1" on a one-stop Day Out with six photos (and "+-5 more").
    totalPhotos = photos.length;
  } else if (type === CONTENT_TYPES.ALBUM) {
    // Albums have photos array
    if (item.photos && Array.isArray(item.photos)) {
      photos.push(...item.photos.slice(0, 10).map(p => ({
        id: p.id,
        url: p.photo_data_base64 || p.url || p.image,
        caption: p.caption,
        display_order: p.display_order
      })));
    }
    // The clips the viewer may watch (V7, feedService → attachAlbumClips) go
    // among the photos in the album's one order; a slide carries its clip so
    // the carousel can play the muted loop and open the vertical viewer.
    const clips = Array.isArray(item.videos) ? item.videos.filter(v => v?.playback) : [];
    if (clips.length > 0) {
      const ordered = photos.every(p => p.display_order != null)
        ? [...photos].sort((a, b) => a.display_order - b.display_order)
        : photos;
      const merged = mergeAlbumMedia(ordered, clips.map(v => ({
        id: v.id,
        url: v.playback.poster_url || v.photo_data_base64 || null,
        media_type: 'video',
        playback: v.playback,
        video: v,
        display_order: v.display_order
      })));
      photos.splice(0, photos.length, ...merged);
    }
    // The cover leads: the cover clip for a viewer who may watch it, else the
    // explicit cover photo. Compare as strings (integer/string from JSON).
    // A cover clip with no slide here (a reader that sends no clips) leaves the
    // cover photo to lead, as before clips.
    const coverKey = item.cover_video?.video_id != null ? `v:${item.cover_video.video_id}` : null;
    let coverIdx = coverKey ? photos.findIndex(p => p.id === coverKey) : -1;
    if (coverIdx === -1 && item.cover_photo_id) coverIdx = photos.findIndex(p => String(p.id) === String(item.cover_photo_id));
    if (coverIdx > 0) {
      const [coverItem] = photos.splice(coverIdx, 1);
      photos.unshift(coverItem);
    }
    if (photos.length > 10) photos.length = 10;
    coverPhoto = item.cover_photo || (photos[0]?.url);
    totalPhotos = (item.photo_count || photos.filter(p => p.media_type !== 'video').length) + clips.length;
  } else if (type === CONTENT_TYPES.EVENT_GROUP || type === CONTENT_TYPES.ARTIST_EVENT || type === CONTENT_TYPES.EVENT) {
    // Events use a single image
    coverPhoto = item.imageUrl || item.image_url || item.image || item.cover_photo;
    totalPhotos = coverPhoto ? 1 : 0;
  } else if (type === CONTENT_TYPES.TOURISM_EXPERIENCE) {
    // Parse stop photos from social feed query (GROUP_CONCAT'd JSON objects)
    if (item.stop_photos_json) {
      const stopPhotos = item.stop_photos_json.split('|||').map((jsonStr, idx) => {
        try {
          const parsed = JSON.parse(jsonStr);
          let url = parsed.photo_url;
          if (!url && parsed.google_photo_ref && parsed.place_id) {
            url = buildGooglePhotoProxyUrl(parsed.google_photo_ref, parsed.place_id);
          }
          if (!url) return null;
          // Credit the row that IS the rendered photo, never the other one.
          // Most of this corpus is CC BY-SA, where attribution is a licence
          // condition — a carousel slide showing one of these must print it.
          // Non-wikimedia winners (metadata/custom images) get the
          // host-derived minimum when their URL is Commons-hosted anyway.
          const attribution = url === parsed.poi_photo_url ? parsed.poi_attribution
            : url === parsed.place_photo_url ? parsed.place_attribution
            : slideAttributionFallback(url);
          return { id: `stop-${idx}`, url, title: parsed.stop_title, order: parsed.stop_order || idx, attribution };
        } catch { return null; }
      }).filter(Boolean);
      photos.push(...stopPhotos);
    }
    // Also support pre-populated stops array (detail page). filter(p => p.url)
    // matches the walking-tour twin above — without it, photoless stops become
    // N empty gradient slides with a photo counter.
    if (!photos.length && item.stops && Array.isArray(item.stops)) {
      photos.push(...item.stops.slice(0, 6).map((stop, idx) => {
        const url = stop.cover_photo || stop.image;
        return {
          id: stop.id || idx,
          url,
          title: stop.name || stop.title,
          order: stop.order_index || idx,
          // Same credit rule as the walking-tour stops fallback above.
          attribution: stop.photoAttribution?.text || slideAttributionFallback(url)
        };
      }).filter(p => p.url));
    }
    // Cover photo: server-resolved cascade (admin → tour_metadata → first-stop
    // place cascade incl. Wikimedia → Google Place) lands in item.cover_photo.
    // Other fields retained for legacy/detail-page code paths.
    coverPhoto = item.cover_photo || item.tour_photo || item.cover_photo_url || item.cover_image;
    totalPhotos = item.stop_count || item.total_stops || photos.length;
  } else if (type === CONTENT_TYPES.VIATOR_EXPERIENCE) {
    coverPhoto = item.cover_image_url || item.cover_photo || item.coverImageUrl;
    totalPhotos = coverPhoto ? 1 : 0;
  } else if (type === CONTENT_TYPES.TIQETS_EXPERIENCE) {
    // Tiqets experiences may carry multiple images (tiqets_experience_images).
    // Backend serializes them as item.images (array of url strings or {url}).
    if (Array.isArray(item.images)) {
      photos.push(...item.images.map((img, idx) => ({
        id: `tiqets-${idx}`,
        url: typeof img === 'string' ? img : (img.url || img.url_large || img.url_medium),
        order: idx
      })).filter(p => p.url));
    }
    coverPhoto = item.cover_image_url || item.cover_photo || item.coverImageUrl || (photos[0]?.url);
    totalPhotos = item.image_count || photos.length || (coverPhoto ? 1 : 0);
  } else if (type === CONTENT_TYPES.VISIT_PHOTO_SET) {
    // Visit photo sets have a photos array from the feed query
    if (item.photos && Array.isArray(item.photos)) {
      photos.push(...item.photos.map((p, idx) => ({
        id: p.id || p.photo_id || idx,
        url: p.url || p.image_data || p.photo_data_base64,
        order: p.order || p.display_order || idx
      })));
    }
    coverPhoto = item.cover_photo || (photos[0]?.url);
    totalPhotos = item.photo_count || item.total_photos || photos.length;
  }

  // Extract photo attribution. Prefer an explicit field carried on the item;
  // otherwise derive the required credit from the cover URL's CDN host (Tiqets
  // affiliate terms, Wikimedia CC) — the feed query emits only a resolved cover
  // URL with no attribution block, so the host is the only signal available
  // here. See attributionForImageUrl. Google photos are credited from the URL
  // by PhotoCredit (PhotoCarousel), never from `googlePhotoAttribution`:
  // that field is always photos[0]'s author, even when the cover came from
  // somewhere else.
  const photoAttribution =
    item.photoAttribution ||
    attributionForImageUrl(coverPhoto) ||
    null;

  return {
    photos: photos.map(p => ({ ...p, url: resolveImageUrl(p.url) })),
    coverPhoto: resolveImageUrl(coverPhoto),
    photoAttribution,
    totalPhotos,
    hasMultiple: photos.length > 1 || totalPhotos > 1
  };
};

/**
 * Normalize engagement data (likes, comments, saves)
 * @param {Object} item - Raw item data
 * @returns {Object} Normalized engagement data
 */
const normalizeEngagement = (item) => {
  return {
    likeCount: item.like_count || item.likeCount || 0,
    commentCount: item.comment_count || item.commentCount || 0,
    saveCount: item.save_count || item.saveCount || 0,
    viewCount: item.view_count || item.viewCount || 0,
    userLiked: item.user_liked || item.userLiked || item.isLiked || false,
    userSaved: item.user_saved || item.userSaved || item.isSaved || false,
    userCommented: item.user_commented || item.userCommented || false,
    socialProof: item.friend_social_proof || null
  };
};

/**
 * Normalize creator data
 * @param {Object} item - Raw item data
 * @returns {Object|null} Normalized creator data, null when nobody is named
 */
const normalizeCreator = (item) => {
  // "Traveler you know" attribution: a friends-trip that reached this feed
  // because a FRIEND is a traveler on it (the owner is a stranger to the
  // viewer) is displayed as that friend — they're the connection that makes
  // the card relevant. The server only sets matched_via_* in exactly that
  // case (owner not in the viewer's friend set); the trip's real owner is
  // unchanged everywhere else (detail page, permissions).
  if (item.matched_via_user_id) {
    return {
      id: item.matched_via_user_id,
      name: item.matched_via_user_name || 'A friend',
      avatar: item.matched_via_user_picture,
      isOwn: false,
      matchedVia: true,
      // The trip's real owner (a stranger to the viewer). Block/Report act on
      // the owner, not the displayed friend, so the overflow menu names them
      // to avoid a confusing mismatch.
      ownerName: item.user_name || item.userName || item.creator_name || null
    };
  }

  const userId = item.user_id || item.userId || item.creator_id || item.ownerId;
  // Null (not an English 'Anonymous' sentinel) when the creator has no name:
  // display sites resolve their own translated fallback
  // (`feedCard:creator.nameFallback`), and the "saved from" guard in
  // UnifiedCard tests truthiness rather than comparing against a literal.
  const userName = item.user_name || item.userName || item.creator_name || item.ownerName || null;
  const userAvatar = item.user_picture || item.user_avatar || item.userAvatar ||
                     item.creator_avatar || item.ownerAvatar;

  // Nobody to name — an event, a catalogue tour (C77) — is no creator, not an
  // anonymous one: no creator row, no "Saved from", no profile link.
  if (!userId && !userName) return null;

  const isOwn = item.is_own_content || item.isOwn || item.is_owned || false;
  return {
    id: userId,
    name: userName,
    avatar: userAvatar,
    isOwn,
    // One-tap Follow on a stranger's post. Only /api/social/feed sends
    // viewer_follows_creator; Home's strips and destination pages read that
    // feed too, so the card shows the button only where the caller asks
    // (showFollow — Discover).
    canFollow: !!userId && !isOwn && item.viewer_follows_creator === false && !item.is_friend_content
  };
};

/**
 * Normalize activity-specific data
 * @param {Object} item - Raw activity data
 * @returns {Object} Normalized activity data
 */
const normalizeActivityData = (item) => {
  const data = item.data || item;

  // Parse Google types from JSON string if needed
  const googleTypes = (() => {
    const raw = data.google_types || data.googleTypes || data.types;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw); } catch { return []; }
  })();

  // Parse user tags/vibes from JSON string if needed
  const parseJsonArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try { return JSON.parse(val); } catch { return []; }
  };

  // Derive the best place type label
  const primaryType = data.primary_type || data.primaryType || googleTypes[0];

  // Memory detection: the post is fresh but the visit isn't. Flag when the
  // activity came through "I've been there before" (is_historical) or when
  // the visit predates the post by more than 30 days ("Did you go?" confirmed
  // months later) — the card labels these with the visit date so recent feed
  // placement reads as "shared recently", not "happened recently".
  const visitDate = data.visit_date || data.visitDate;
  const visitDatePrecision = data.visit_date_precision || data.visitDatePrecision || 'exact';
  const postedAt = data.shared_at || data.sharedAt || data.created_at || data.createdAt;
  const MEMORY_GAP_MS = 30 * 24 * 60 * 60 * 1000;
  const isMemory = !!(data.is_historical || data.isHistorical) ||
    !!(visitDate && postedAt &&
       (new Date(postedAt) - parseLocalDate(visitDate)) > MEMORY_GAP_MS);

  return {
    placeId: data.place_id || data.placeId || item.contentId || item.content_id,
    category: data.category || item.category || 'other',
    categoryIconName: getCategoryIconName(data.category || item.category),
    categoryLabel: getCategoryDisplayName(data.category || item.category),
    rating: data.rating || item.place_rating,
    priceLevel: data.price_level || data.priceLevel || data.price,
    isEvent: data.is_event || data.source === 'ticketmaster' || !!data.event_date,
    eventDate: data.event_date || data.eventDate,
    eventTime: data.event_time || data.eventTime,
    venue: data.venue_name || data.venue,
    city: data.city,
    url: data.url,
    source: data.source || 'unknown',
    matchReason: data.match_reason || data.matchReason,
    matchType: data.match_type || data.matchType,
    matchedArtist: data.matched_artist || data.matchedArtist,
    matchedEntityId: data.matched_entity_id || data.matchedEntityId,
    matchedVia: data.matched_via || data.matchedVia,
    // New place detail fields
    primaryType,
    googleTypes,
    googleRating: data.google_rating || data.googleRating || data.rating,
    googleRatingCount: data.google_rating_count || data.googleRatingCount || data.user_ratings_total || data.userRatingsTotal,
    editorialSummary: data.editorial_summary || data.editorialSummary,
    goodForChildren: data.good_for_children || data.goodForChildren,
    isFamilyFriendly: data.is_family_friendly || data.isFamilyFriendly,
    isHiddenGem: data.is_hidden_gem || data.isHiddenGem,
    userTags: parseJsonArray(data.user_tags || data.userTags),
    userVibes: parseJsonArray(data.user_vibes || data.userVibes),
    visitDate,
    visitDatePrecision,
    isMemory
  };
};

/**
 * Normalize collection-specific data
 * @param {Object} item - Raw collection data
 * @returns {Object} Normalized collection data
 */
const normalizeCollectionData = (item) => {
  return {
    itemCount: item.item_count || 0,
    categoryBreakdown: item.category_breakdown || {},
    primaryLocation: item.primary_location,
    visibility: item.visibility || 'private'
  };
};

/**
 * Normalize board-specific data
 * @param {Object} item - Raw board data
 * @returns {Object} Normalized board data
 */
const normalizeBoardData = (item) => {
  return {
    itemCount: item.item_count || 0,
    typeBreakdown: item.type_breakdown || {},
    visibility: item.visibility || 'private'
  };
};

/**
 * Normalize walking tour-specific data
 * @param {Object} item - Raw walking tour data
 * @returns {Object} Normalized walking tour data
 */
const normalizeWalkingTourData = (item) => {
  return {
    stopCount: item.total_stops || item.stop_count || item.stops?.length || 0,
    duration: item.estimated_walking_minutes || item.estimated_duration || item.duration,
    distance: item.total_distance_meters || item.total_distance || item.distance,
    difficulty: item.difficulty_level || item.difficulty || 'moderate',
    startPoint: item.start_point || item.startLocation,
    tripId: item.trip_id
  };
};

/**
 * Normalize trip-specific data
 * @param {Object} item - Raw trip data
 * @returns {Object} Normalized trip data
 */
const normalizeTripData = (item) => {
  const data = item.data || item;

  const startDate = data.startDate || data.start_date;
  const endDate = data.endDate || data.end_date;
  // Day COUNT, not a formatted string: this module is a pure normalizer with
  // no `t()` in scope, and the label has to pluralize per locale. TripContent
  // renders it via t('tripContent.duration', { count }).
  let durationDays = null;

  if (startDate && endDate) {
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    durationDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  }

  return {
    startDate,
    endDate,
    durationDays,
    travelerCount: data.travelers?.length || data.companions?.length || 0,
    activityCount: data.activity_count || data.activities?.length || 0,
    status: data.status || 'planning',
    myFeedbackCompleted: data.myFeedbackCompleted ?? data.my_feedback_completed ?? false,
    copyCount: data.copy_count ?? data.copyCount ?? 0,
    friendsCopyCount: data.friends_copy_count ?? data.friendsCopyCount ?? 0,
    // "Inspired by" provenance summary (destination direction). Feed batches it
    // as provenance_summary; getTripById attaches the full provenance object —
    // accept either shape, normalize to { total, topSource }.
    provenanceSummary: data.provenance_summary
      ?? (data.provenance ? { total: data.provenance.total, topSource: data.provenance.topSource } : null)
  };
};

/**
 * Normalize album-specific data
 * @param {Object} item - Raw album data
 * @returns {Object} Normalized album data
 */
const normalizeAlbumData = (item) => {
  let dayNumbers = null;
  if (item.trip_day_ids) {
    try {
      const ids = typeof item.trip_day_ids === 'string'
        ? JSON.parse(item.trip_day_ids)
        : item.trip_day_ids;
      if (Array.isArray(ids) && ids.length > 0) dayNumbers = ids;
    } catch (err) { /* ignore */ }
  }
  // Append "Day N of [Trip]" hint to tripName when this is a day-scoped recap.
  let tripName = item.trip_name;
  if (dayNumbers && tripName && Array.isArray(item.day_indexes) && item.day_indexes.length > 0) {
    const sorted = [...item.day_indexes].sort((a, b) => a - b);
    const span = sorted.length === 1
      ? `Day ${sorted[0]}`
      : `Days ${sorted[0]}–${sorted[sorted.length - 1]}`;
    tripName = `${span} of ${tripName}`;
  }
  // Display city cascade: confirmed_city > region > trips.location.
  // When both city and region exist and differ, combine ("Valletta, Malta").
  const city = item.display_city || null;
  const region = item.display_region || null;
  let displayLocation;
  if (city && region && city.toLowerCase() !== region.toLowerCase()) {
    displayLocation = `${city}, ${region}`;
  } else if (city) {
    displayLocation = city;
  } else if (region) {
    displayLocation = region;
  } else {
    displayLocation = item.trip_location || null;
  }

  return {
    photoCount: item.photo_count || item.photos?.length || 0,
    placeCount: item.place_count || 0,
    tripId: item.trip_id,
    tripName,
    tripLocation: displayLocation,
    tripStartDate: item.trip_start_date?.trim() || null,
    tripEndDate: item.trip_end_date?.trim() || null,
    tripDayIds: dayNumbers,
    isRecap: !!dayNumbers,
    // A Day Out's recap: the trip IS the day, so "from trip <weekday>" says
    // nothing the card doesn't already.
    isDayOut: item.trip_type === 'day_out',
    // Listed inside its own trip (the trip's album list): the trip name
    // still names an untitled card, but "from trip <name>" would repeat it.
    listedInOwnTrip: !!item.listed_in_own_trip,
    // The raw parts of an UNTITLED recap's headline (the feed sends them only
    // when the title is blank). Deliberately unformatted — this module is not
    // i18n-migrated, and a date must not be frozen in English here; the card
    // renders them with recapFallbackTitle.
    recapDates: Array.isArray(item.recap_dates) ? item.recap_dates : null,
    headlinePlaces: Array.isArray(item.headline_places) ? item.headline_places : null,
    visibility: item.visibility || 'private'
  };
};

/**
 * Normalize tourism experience-specific data
 * @param {Object} item - Raw tourism experience data
 * @returns {Object} Normalized tourism experience data
 */
const normalizeTourismExperienceData = (item) => {
  return {
    stopCount: item.total_stops || item.stop_count || item.stops?.length || 0,
    duration: item.typical_duration_minutes || item.estimated_duration || item.duration,
    distance: item.total_distance_meters || item.total_distance || item.distance,
    difficulty: item.difficulty_level || item.difficulty || 'moderate',
    transportMode: item.transport_mode || item.transportMode,
    routeType: item.route_type || item.routeType,
    experienceCategory: item.experience_category || item.experienceCategory,
    operatorName: item.operator_name || item.operatorName,
    isCanonical: item.is_canonical || item.isCanonical || false,
    yearRound: item.year_round !== undefined ? item.year_round : (item.yearRound !== undefined ? item.yearRound : true),
    operatingSeasonStart: item.operating_season_start || item.operatingSeasonStart,
    operatingSeasonEnd: item.operating_season_end || item.operatingSeasonEnd,
    priceRangeMin: item.price_range_min || item.priceRangeMin,
    priceRangeMax: item.price_range_max || item.priceRangeMax,
    priceCurrency: item.price_currency || item.priceCurrency || 'USD',
    familyScore: item.family_score || item.familyScore,
    coverPhotoUrl: item.cover_photo_url || item.coverPhotoUrl
  };
};

/**
 * Normalize Viator experience-specific data
 * @param {Object} item - Raw Viator product data
 * @returns {Object} Normalized Viator experience data
 */
const normalizeViatorExperienceData = (item) => {
  return {
    productCode: item.product_code || item.productCode,
    rating: item.rating ? parseFloat(item.rating) : null,
    reviewCount: item.review_count || item.reviewCount || 0,
    priceFrom: item.price_from || item.priceFrom,
    priceCurrency: item.price_currency || item.priceCurrency || 'EUR',
    durationMinutes: item.duration_minutes || item.durationMinutes,
    durationText: item.duration_text || item.durationText,
    primaryCategory: item.primary_category || item.primaryCategory,
    freeCancellation: !!(item.free_cancellation || item.freeCancellation),
    likelyToSellOut: !!(item.likely_to_sell_out || item.likelyToSellOut),
    privateTour: !!(item.private_tour || item.privateTour),
    skipTheLine: !!(item.skip_the_line || item.skipTheLine),
    familyFriendly: !!(item.family_friendly || item.familyFriendly),
    affiliateUrl: item.affiliate_url || item.affiliateUrl,
    destinationName: item.destination_name || item.destinationName,
    country: item.country,
    coverImageUrl: item.cover_image_url || item.coverImageUrl
  };
};

/**
 * Normalize Tiqets experience data (affiliate, city-scoped, see Viator for the
 * shape this mirrors). Option-B schedule fields: durationText/durationMinutes
 * from product duration; isRecurringShow + scheduleChips set by the backend
 * from the Tiqets sub-category bucket + time-of-day / seasonal tags. We never
 * try to invent a specific event_date/event_time — Tiqets shows are recurring
 * series; the booking date is picked on Tiqets at checkout.
 *
 * @param {Object} item - Raw Tiqets experience row (joined w/ image + cheapest product)
 * @returns {Object} Normalized Tiqets experience data
 */
const normalizeTiqetsExperienceData = (item) => {
  return {
    experienceId: item.experience_id || item.experienceId,
    rating: item.rating != null ? parseFloat(item.rating) : null,
    reviewCount: item.rating_count || item.reviewCount || 0,
    priceFrom: item.from_price_cents != null ? item.from_price_cents / 100 : (item.priceFrom ?? null),
    // Curated free-general-admission venue: priceFrom is an add-on (audio
    // guide / tour), not an entry fee — cards label it accordingly. Absent on
    // older payloads → falsy → normal "From" label.
    freeAdmission: !!(item.free_admission || item.freeAdmission),
    priceCurrency: item.currency || item.priceCurrency || 'EUR',
    durationText: item.duration_text || item.durationText || null,
    durationMinutes: item.duration_minutes || item.durationMinutes || null,
    // Option B: structured schedule hints derived from Tiqets tags. The backend
    // populates these; the frontend just renders the chips.
    isRecurringShow: !!(item.is_recurring_show || item.isRecurringShow),
    scheduleChips: Array.isArray(item.schedule_chips) ? item.schedule_chips
                  : (Array.isArray(item.scheduleChips) ? item.scheduleChips : []),
    primaryCategory: item.primary_category || item.primaryCategory || null,
    affiliateUrl: item.experience_url || item.affiliateUrl || null,
    city: item.city_name || item.city || null,
    country: item.country_name || item.country || null,
    address: item.address || null,
    coverImageUrl: item.cover_image_url || item.coverImageUrl || null
  };
};

/**
 * Normalize visit photo set data (multi-photo posts with captions)
 * @param {Object} item - Raw visit photo set data
 * @returns {Object} Normalized photo set data
 */
const normalizeVisitPhotoSetData = (item) => {
  return {
    photoCount: item.photo_count || item.total_photos || 0,
    placeId: item.place_id,
    activityId: item.activity_id,
    rating: item.place_rating || item.rating,
    wouldReturn: item.would_return,
    wouldRecommend: item.would_recommend,
    category: item.category,
    city: item.city
  };
};

// Ticketmaster and paris_opendata encode "ends after midnight" as
// end_date = event_date + 1 (an 8pm show ending 1am): ~1,200 feed-eligible
// single-evening rows vs ~50 genuine two-day events at last count, and no
// event payload carries end_time to disambiguate. So a +1-day span reads as
// the same evening and is dropped; only 2+ day spans (UitDatabank expos,
// festival runs) surface as a date range on the card badge.
// Open-ended offerings (UiT permanent venues) store a far-future sentinel
// end_date so the app's "is this still upcoming?" reads stay correct — see
// eventSyncService's OPEN_ENDED_END_DATE. That is a storage device, not a
// date anyone should see: rendered as a range it reads "Sep 6 - Dec 31, 2099".
// A span longer than a year means "ongoing", so drop it and let the card fall
// back to a single date. Also catches UiT's own 2049/2050 sentinels if one
// ever reaches the card layer. Step 2 replaces the single date with a
// frequency badge ("Open daily" / "Saturdays") — docs/PERPETUAL_VENUE_DECISIONS.md §3d.
const OPEN_ENDED_SPAN_DAYS = 366;

const multiDayEndDate = (startRaw, endRaw) => {
  if (!startRaw || !endRaw) return null;
  const spanDays = Math.round(
    (parseLocalDate(endRaw) - parseLocalDate(startRaw)) / 86400000
  );
  // NaN (unparseable either side) fails both comparisons and drops too.
  return spanDays >= 2 && spanDays <= OPEN_ENDED_SPAN_DAYS ? endRaw : null;
};

// Ticketmaster writes the literal placeholder "See tickets" into
// price_display when no price exists (~98% of upcoming rows). Next to the
// card's actual Tickets link that string is pure noise, so it is dropped at
// normalization — the rule the retired ShowCard enforced with isRealPrice().
const realPriceDisplay = (p) => {
  if (typeof p !== 'string') return p ?? null;
  const trimmed = p.trim();
  return trimmed && trimmed.toLowerCase() !== 'see tickets' ? p : null;
};

/**
 * Normalize event group-specific data (theater runs, festivals, concert residencies)
 * @param {Object} item - Raw event group data
 * @returns {Object} Normalized event group data
 */
const normalizeEventGroupData = (item) => {
  const data = item.data || item;
  // A group whose members are open-ended venues has no meaningful date either:
  // event_groups.start_date is MIN(event_date), i.e. the sync date. Same
  // treatment as the single-event card — see normalizeEventData.
  const rawGroupCustom = data.customAttributes || data.custom_attributes;
  let groupCustom = null;
  if (rawGroupCustom) {
    if (typeof rawGroupCustom === 'string') {
      try { groupCustom = JSON.parse(rawGroupCustom); } catch (_) { groupCustom = null; }
    } else if (typeof rawGroupCustom === 'object') {
      groupCustom = rawGroupCustom;
    }
  }
  return {
    frequency: deriveVenueFrequency(groupCustom),
    groupId: data.groupId || data.group_id || data.id,
    groupType: data.groupType || data.group_type,
    eventCount: data.eventCount || data.event_count || 0,
    startDate: data.startDate || data.start_date || data.eventDate || data.event_date,
    endDate: data.endDate || data.end_date,
    venue: data.venue_name || data.venue,
    venueId: data.venue_id || data.venueId,
    city: data.city,
    state: data.state,
    countryCode: data.country_code || data.countryCode,
    priceMin: data.priceMin || data.price_min,
    priceMax: data.priceMax || data.price_max,
    // price_display fallback: the follow-bands items carry only the display
    // string (see followingService._venueItem), never structured min/max.
    price: realPriceDisplay(data.price || data.price_display),
    // Source attribution (UitInVlaanderenCredit). Unlike single events there
    // is no 'ticketmaster' fallback: a group row without source_type must not
    // accidentally earn (or dodge) a credit.
    source: data.source || data.source_type || null,
    // "Newly announced" pill (CardBadges.NewBadge) — set by the surface on
    // the raw item, never derived here (What's On pins vs band 1 differ).
    isNew: !!data._isNew,
    matchType: data.match_type || data.matchType || null,
    matchReason: data.match_reason || data.matchReason || null,
    matchedArtist: data.matched_entity || data.matchedArtist || null,
    matchedEntityId: data.matched_entity_id || data.matchedEntityId || null
  };
};

/**
 * Normalize artist event-specific data (events matching user's followed artists)
 * @param {Object} item - Raw artist event data
 * @returns {Object} Normalized artist event data
 */
const normalizeArtistEventData = (item) => {
  const data = item.data || item;
  return {
    eventId: data.event_id || data.id,
    eventDate: data.event_date || data.eventDate || data.date,
    eventTime: data.event_time || data.eventTime || data.time,
    venue: data.venue_name || data.venue,
    venueId: data.venue_id || data.venueId,
    city: data.city,
    state: data.state,
    countryCode: data.country_code,
    matchType: data.match_type || 'direct',
    matchReason: data.match_reason,
    matchedArtist: data.matched_artist,
    matchedEntityId: data.matched_entity_id || data.matchedEntityId,
    matchedVia: data.matched_via,
    artists: data.artists || [],
    // Destination order is deliberate (see the ticket_url-first note above/below), but
    // each candidate must actually be a URL: some feeds put prose in
    // `ticket_url` ("Gratuit sur réservation"), which would otherwise become an
    // href pointing at a route that doesn't exist. See utils/eventLinks.js.
    ticketUrl: resolveEventUrl(data) || (isNavigableUrl(data.url) ? data.url : null),
    // Whether that destination is a real BOOKING url, so the card can label it
    // honestly. Collapsing the two fields into `ticketUrl` above means the
    // label can't tell them apart on its own — 212 of the ticketless
    // UitDatabank events are free, and "Get Tickets" on a free grief-support
    // session is simply wrong.
    hasTicketUrl: isTicketUrl(data),
    distanceKm: data.distance_km,
    isNew: !!data._isNew,
    // Follow-chip state: the chip renders whatever it's told (it never
    // fetches), so surfaces that KNOW the answer must say so. Payloads that
    // don't carry it (the ranked feed) keep the not-following default.
    venueIsFollowing: !!data.venue_is_following,
    hideVenueFollow: !!data._hideVenueFollow
  };
};

/**
 * Normalize generic event-specific data (Ticketmaster events, etc.)
 * @param {Object} item - Raw event data
 * @returns {Object} Normalized event data
 */
const normalizeEventData = (item) => {
  const data = item.data || item;

  // custom_attributes arrives parsed from /search (customAttributes) or as a raw
  // JSON string from the feed/other paths (custom_attributes). Normalize to an object.
  const rawCustom = data.customAttributes || data.custom_attributes;
  let customAttributes = null;
  if (rawCustom) {
    if (typeof rawCustom === 'string') {
      try { customAttributes = JSON.parse(rawCustom); } catch (_) { customAttributes = null; }
    } else if (typeof rawCustom === 'object') {
      customAttributes = rawCustom;
    }
  }

  return {
    eventId: data.id || data.event_id,
    eventDate: data.date || data.event_date || data.eventDate,
    eventTime: data.time || data.event_time || data.eventTime,
    // Genuine multi-day runs only — midnight rollovers and same-day rows
    // come through as null (see multiDayEndDate above). The badge renders
    // a range when this survives, a single date otherwise.
    endDate: multiDayEndDate(
      data.date || data.event_date || data.eventDate,
      data.end_date || data.endDate
    ),
    // Open-ended venues have no meaningful date — their event_date is the last
    // sync date. Non-null here means the badge should say how often the place
    // is open ("Open daily" / "Saturdays") instead of naming a day nobody
    // should act on. Null for every ordinary dated event.
    frequency: deriveVenueFrequency(customAttributes),
    venue: data.venue_name || data.venue,
    venueId: data.venue_id || data.venueId,
    city: data.city,
    state: data.state,
    countryCode: data.country_code,
    category: data.category || data.genre,
    // More specific genre when available. Field name varies by path: feed/recs
    // use `genre`; /search exposes it as `subcategory` (from secondary_category).
    genre: data.genre || data.subcategory || data.secondary_category || null,
    description: data.description || '',
    // /search maps price_display -> price; the feed sends price_display directly.
    price: realPriceDisplay(data.price || data.price_display || data.priceDisplay),
    // Structured price so the card formats with proper currency symbols via
    // formatCurrencyRange, instead of rendering a baked "USD25.5-30.5" string.
    priceMin: data.price_min ?? data.priceMin ?? null,
    priceMax: data.price_max ?? data.priceMax ?? null,
    priceCurrency: data.price_currency || data.priceCurrency || 'USD',
    // ticket_url FIRST: UiTdatabank stores the booking link there and the info
    // page in external_url, and a source that affiliate-tags its links tags
    // ticket_url.
    // (normalizeArtistEventData above always had this order.)
    // Destination order is deliberate (see the ticket_url-first note above/below), but
    // each candidate must actually be a URL: some feeds put prose in
    // `ticket_url` ("Gratuit sur réservation"), which would otherwise become an
    // href pointing at a route that doesn't exist. See utils/eventLinks.js.
    ticketUrl: resolveEventUrl(data) || (isNavigableUrl(data.url) ? data.url : null),
    // Whether that destination is a real BOOKING url, so the card can label it
    // honestly. Collapsing the two fields into `ticketUrl` above means the
    // label can't tell them apart on its own — 212 of the ticketless
    // UitDatabank events are free, and "Get Tickets" on a free grief-support
    // session is simply wrong.
    hasTicketUrl: isTicketUrl(data),
    source: data.source || data.source_type || 'ticketmaster',
    customAttributes,
    matchType: data.match_type || item.match_type || null,
    matchedArtist: data.matched_entity || item.matched_entity || null,
    matchedEntityId: data.matched_entity_id || item.matched_entity_id || null,
    matchReason: data.match_reason || item.match_reason || null,
    isNew: !!data._isNew,
    // Follow-chip state — same contract as normalizeArtistEventData above.
    venueIsFollowing: !!data.venue_is_following,
    hideVenueFollow: !!data._hideVenueFollow
  };
};

/**
 * Main normalization function - converts any content type to unified format
 *
 * @param {Object} item - Raw item data from API or database
 * @param {string} type - Content type (activity, collection, board, walking_tour, trip, album)
 * @param {string} context - Display context (discover, saved, board, trip, search)
 * @returns {Object} Normalized card data
 */
export const normalizeCardData = (item, type, context = CARD_CONTEXTS.DISCOVER) => {
  if (!item) return null;

  // Handle nested data structure (savedItem.data pattern)
  const baseData = item.data || item;

  // Common fields across all types
  const id = item.id || baseData.id || item.activity_id || item.contentId;
  const title = baseData.name || baseData.title || item.displayName
    || item.corrected_place_name || item.place_name || 'Untitled';
  const description = baseData.description || baseData.notes || '';
  const location = baseData.location || item.place_address
    || baseData.city || baseData.venue || '';

  // Timestamps
  const createdAt = item.created_at || item.createdAt || baseData.created_at;
  const savedAt = item.savedAt || item.saved_at;
  const sharedAt = item.shared_at || item.sharedAt;

  // Tags
  const tags = item.tags || baseData.tags || [];

  // Build normalized data object
  const normalized = {
    // Ephemeral presentation metadata; never provider provenance or scores.
    recommendation: item._recommendation || null,
    followSource: item.follow_source || null,
    // Core identifiers
    id,
    type,
    context,

    // Basic info
    title,
    description,
    location,
    tags,

    // Timestamps
    createdAt,
    savedAt,
    sharedAt,
    timeAgo: formatTimeAgo(sharedAt || createdAt),

    // Photo/Carousel data
    carousel: normalizeCarouselData(item, type),

    // Engagement metrics
    engagement: normalizeEngagement(item),

    // Creator info
    creator: normalizeCreator(item),

    // Type badge
    badge: getTypeBadge(type),

    // Placeholder gradient
    gradient: getTypeGradient(type),

    // Type-specific data
    typeData: null,

    // Detail URL (for navigation)
    detailUrl: null,

    // Sponsored content flags
    isSponsored: item._isSponsored || item.is_sponsored || false,
    sponsorName: item._sponsorName || item.sponsor_name || null,
    sponsoredContentId: item._sponsoredContentId || item.sponsored_content_id || null,
    sponsoredToken: item._sponsoredToken || item.sponsored_token || null,
    // Rank/context annotations for viewport impression reporting
    sponsoredOrganicRank: Number.isInteger(item._organicRank) ? item._organicRank : null,
    sponsoredBoostedRank: Number.isInteger(item._boostedRank) ? item._boostedRank : null,
    sponsoredQueryContext: item._queryContext || null,

    // Context insight chip (feed Option B) — server attaches only when the
    // viewer's current weather/day matches this place's visit pattern
    contextChip: item.context_chip || null,

    // Original raw data (for handlers that need it)
    _raw: item
  };

  // Add type-specific data and URLs
  switch (type) {
    case CONTENT_TYPES.ACTIVITY: {
      normalized.typeData = normalizeActivityData(item);
      // Override generic "Activity" badge with specific place type
      const placeTypeLabel = normalized.typeData.primaryType
        ? normalized.typeData.primaryType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : null;
      if (placeTypeLabel) {
        normalized.badge = {
          ...normalized.badge,
          label: placeTypeLabel,
          icon: getCategoryIconName(normalized.typeData.primaryType)
        };
      }
      // Resolve the best ID: prefer activity_id, then id, then content_id, then placeId
      const activityDetailId = item.activity_id || baseData.activity_id
        || id || item.content_id || baseData.content_id
        || normalized.typeData.placeId;
      // User-created activities (act_ prefix) go to activity detail;
      // bare Place IDs go to the place info page
      if (typeof activityDetailId === 'string' && activityDetailId.startsWith('act_')) {
        normalized.detailUrl = `/activity/${activityDetailId}`;
      } else if (typeof activityDetailId === 'string' && activityDetailId.startsWith('atr_')) {
        // Entity-spine ids (landmarks, attractions) now have a page:
        // /place/:id accepts both id shapes and resolves an 'atr_' one through
        // the crosswalk. This used to be null whenever the spine held no
        // Google place link, which was 99.7% of photographed landmarks — the
        // card rendered a photo, a name and a description and then did
        // nothing when tapped.
        //
        // The ATTRACTION id is canonical, so this deliberately does NOT swap
        // in the crosswalked place_id when one exists. Two reasons: the
        // attraction is the identity that engagement and saves key on, and the
        // server now counts likes/saves/visits across the whole equivalent-id
        // set, so /place/atr_… and /place/ChIJ… show the same state for the
        // same real place. Without that expansion this choice would fork the
        // like count.
        normalized.detailUrl = `/place/${activityDetailId}`;
        normalized.creator = null;
      } else {
        normalized.detailUrl = `/place/${activityDetailId}`;
        // Place cards are shared entities — never show a user as the creator
        normalized.creator = null;
      }
      break;
    }

    case CONTENT_TYPES.COLLECTION:
      normalized.typeData = normalizeCollectionData(item);
      normalized.detailUrl = `/collection/${id}`;
      break;

    case CONTENT_TYPES.BOARD:
      normalized.typeData = normalizeBoardData(item);
      normalized.detailUrl = `/board/${id}`;
      break;

    case CONTENT_TYPES.WALKING_TOUR:
      normalized.typeData = normalizeWalkingTourData(item);
      normalized.detailUrl = `/walking-tours/${id}`;
      break;

    case CONTENT_TYPES.TRIP:
      normalized.typeData = normalizeTripData(item);
      // Use /trip for own trips, /shared/trip for others
      normalized.detailUrl = normalized.creator?.isOwn
        ? `/trip/${id}`
        : `/shared/trip/${id}`;
      break;

    case CONTENT_TYPES.ALBUM:
      normalized.typeData = normalizeAlbumData(item);
      // A recap's title is optional. Keep a blank one blank — the generic
      // 'Untitled' above would hide the day-and-places name the card builds
      // (albumCardTitle), which is what the composer promised its author.
      if (!(baseData.name || baseData.title)) normalized.title = '';
      normalized.detailUrl = `/album/${id}`;
      break;

    case CONTENT_TYPES.EVENT_GROUP: {
      normalized.typeData = normalizeEventGroupData(item);
      const groupId = normalized.typeData.groupId || id;
      normalized.detailUrl = `/event-group/${encodeURIComponent(groupId)}`;
      break;
    }

    case CONTENT_TYPES.ARTIST_EVENT: {
      normalized.typeData = normalizeArtistEventData(item);
      // Card click goes INTO the app when there is somewhere to go (the venue
      // page); the ticket URL is the ActionRow's explicit Tickets link, not
      // the card's destination. Venue-less events fall back to clicking out.
      if (normalized.typeData.venueId) {
        normalized.detailUrl = `/venues/${encodeURIComponent(normalized.typeData.venueId)}`;
      } else {
        normalized.detailUrl = normalized.typeData.ticketUrl || null;
        normalized.externalLink = !!normalized.typeData.ticketUrl;
      }
      break;
    }

    case CONTENT_TYPES.EVENT: {
      normalized.typeData = normalizeEventData(item);
      // Same internal-first rule as ARTIST_EVENT above.
      if (normalized.typeData.venueId) {
        normalized.detailUrl = `/venues/${encodeURIComponent(normalized.typeData.venueId)}`;
      } else {
        normalized.detailUrl = normalized.typeData.ticketUrl || null;
        normalized.externalLink = !!normalized.typeData.ticketUrl;
      }
      // Events have no user owner — suppress creator row
      normalized.creator = null;
      break;
    }

    case CONTENT_TYPES.TOURISM_EXPERIENCE: {
      normalized.typeData = normalizeTourismExperienceData(item);
      normalized.detailUrl = `/tourism-experience/${id}`;
      // Admin-sourced (seeded) tourism experiences have no creator attribution
      if (item.tour_source === 'admin' || item.is_canonical === 1 || item.is_canonical === true) {
        normalized.creator = null;
      }
      // Override badge emoji to match transport mode. Both maps come from the
      // shared config — the local copies had drifted, missing `chairlift` and
      // `park_circuit` and still naming a `glacier_hike` category that does
      // not exist.
      const transportEmojis = TRANSPORT_PRESENTATION;
      // Hike/trek categories get a hiking boot instead of generic walking emoji
      const hikeCategories = FOOT_CATEGORIES;
      const category = normalized.typeData?.experienceCategory;
      const mode = normalized.typeData?.transportMode;
      // Override `icon` alongside `emoji`: PlaceholderImage renders badge.icon,
      // and the type default is 'train' — without this, every photoless
      // non-rail experience (a trek, a rowboat tour) shows a train.
      if (hikeCategories.includes(category)) {
        normalized.badge = { ...normalized.badge, emoji: '🥾', icon: 'mountain' };
      } else if (mode && transportEmojis[mode]?.emoji) {
        normalized.badge = {
          ...normalized.badge,
          emoji: transportEmojis[mode].emoji,
          icon: transportEmojis[mode].icon || normalized.badge.icon
        };
      }
      break;
    }

    case CONTENT_TYPES.VIATOR_EXPERIENCE:
      normalized.typeData = normalizeViatorExperienceData(item);
      normalized.detailUrl = normalized.typeData.affiliateUrl || null;
      normalized.externalLink = true;
      normalized.creator = null;
      break;

    case CONTENT_TYPES.TIQETS_EXPERIENCE:
      normalized.typeData = normalizeTiqetsExperienceData(item);
      normalized.detailUrl = normalized.typeData.affiliateUrl || null;
      normalized.externalLink = true;
      normalized.creator = null;
      break;

    case CONTENT_TYPES.VISIT_PHOTO_SET: {
      normalized.typeData = normalizeVisitPhotoSetData(item);
      const activityDetailId = item.activity_id || id;
      normalized.detailUrl = `/activity/${activityDetailId}`;
      break;
    }

    default:
      normalized.detailUrl = `/activity/${id}`;
  }

  return normalized;
};

/**
 * Batch normalize multiple items
 * @param {Array} items - Array of raw items
 * @param {string} type - Content type for all items
 * @param {string} context - Display context
 * @returns {Array} Array of normalized items
 */
export const normalizeCardDataBatch = (items, type, context = CARD_CONTEXTS.DISCOVER) => {
  if (!items || !Array.isArray(items)) return [];
  return items.map(item => normalizeCardData(item, type, context)).filter(Boolean);
};

/**
 * Map a raw type string to a CONTENT_TYPES enum value.
 * Handles both exact matches ('activity') and underscore variants ('walking_tour').
 * Returns ACTIVITY as fallback for unknown types.
 *
 * @param {string} rawType - Raw type string (e.g. 'activity', 'walking_tour', 'trip')
 * @returns {string} CONTENT_TYPES enum value
 */
export const getContentTypeEnum = (rawType) => {
  if (!rawType) return CONTENT_TYPES.ACTIVITY;
  // Direct match against enum values
  if (Object.values(CONTENT_TYPES).includes(rawType)) return rawType;
  return CONTENT_TYPES.ACTIVITY;
};

/**
 * Flatten a board item (with content_details) into the shape normalizeCardData expects.
 * Board items from the API come as { content_id, content_type, content_details: {...}, added_at }.
 * This merges content_details to the top level so normalizeCardData can handle it.
 *
 * @param {Object} boardItem - Raw board item with content_details
 * @returns {Object} Flattened item suitable for normalizeCardData
 */
export const flattenBoardItem = (boardItem) => {
  if (!boardItem?.content_details) return null;
  const details = boardItem.content_details;
  return {
    ...details,
    id: boardItem.content_id,
    saved_at: boardItem.added_at,
    content_type: boardItem.content_type
  };
};

/**
 * Determine content type from raw item data
 * @param {Object} item - Raw item data
 * @returns {string} Detected content type
 */
export const detectContentType = (item) => {
  if (!item) return CONTENT_TYPES.ACTIVITY;

  // Check explicit type fields
  if (item.content_type || item.contentType) {
    const type = item.content_type || item.contentType;
    if (Object.values(CONTENT_TYPES).includes(type)) {
      return type;
    }
  }

  // Infer from flags set by SocialFeed
  if (item._isEventGroup || item.isGroup || item.group_type) return CONTENT_TYPES.EVENT_GROUP;
  if (item._isArtistEvent) return CONTENT_TYPES.ARTIST_EVENT;
  if (item._isEvent) return CONTENT_TYPES.EVENT;

  // Infer from data structure
  if (item.tiqets_product_code || item.tiqetsProductCode) return CONTENT_TYPES.TIQETS_EXPERIENCE;
  if (item.product_code || item.productCode) return CONTENT_TYPES.VIATOR_EXPERIENCE;
  if (item.experience_category || item.transport_mode || item.transportMode) return CONTENT_TYPES.TOURISM_EXPERIENCE;
  if (item.stop_count || item.stops) return CONTENT_TYPES.WALKING_TOUR;
  if (item.photo_count && !item.place_id) return CONTENT_TYPES.ALBUM;
  if (item.travelers || item.companions || item.start_date) return CONTENT_TYPES.TRIP;
  if (item.type_breakdown || item.preview_items) return CONTENT_TYPES.BOARD;
  if (item.category_breakdown || item.preview_activities) return CONTENT_TYPES.COLLECTION;

  return CONTENT_TYPES.ACTIVITY;
};

/**
 * Auto-normalize item by detecting type
 * @param {Object} item - Raw item data
 * @param {string} context - Display context
 * @returns {Object} Normalized card data
 */
export const autoNormalizeCardData = (item, context = CARD_CONTEXTS.DISCOVER) => {
  const type = detectContentType(item);
  return normalizeCardData(item, type, context);
};

export default normalizeCardData;
